import {computed, inject, Injectable, Signal} from '@angular/core';
import {AuthenticationService} from '@authentication/authentication.service';
import {OrganizationResponse, UserResponse} from '@middleware';
import {OrgService} from '@services/organization/org.service'; // Existing service
import {UserService} from '@services/user/user.service'; // Existing service
/**
 * Convenience service that provides all critical authentication-related data as Signals
 * as a singleton throughout the app. This ensures that consumers of auth state do not need
 * to individually call services or convert Resources/Promises/Observables to Signals.
 *
 * This service depends on UserService and OrgService to get user and organization data, which,
 * in turn, handle the actual API calls and token management.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthDataService {
  // Use inject() to get services at the class property level
  protected userService = inject(UserService);
  protected orgService = inject(OrgService);
  protected authenticationService = inject(AuthenticationService);
  public readonly userResource = this.userService.user;

  public readonly me: Signal<UserResponse | null | undefined> = this.userResource.value;
  public readonly isTokenPresent = computed(() => !!this.authenticationService.token());

  public readonly isUserLoggedIn = computed(() => {
    return this.userResource.hasValue() && this.me() !== null;
  });

  // Derive orgs from another signal within OrgService
  public readonly organizationArray = computed(() => this.orgService.organizationArray());

  // Public Org Data (remains a computed signal deriving state from another signal)
  public readonly publicOrg: Signal<OrganizationResponse | undefined> = computed(() => {
    const orgs = this.organizationArray();
    if (!orgs || orgs.length < 1) return undefined;
    return orgs.find(org => org.full_name === 'Other/External');
  });

  // Relevant Organization ID Signal
  public readonly relevantOrgId = computed(() => {
    return this.me()?.organization?.id ?? this.publicOrg()?.id;
  });

  constructor() {
    // FIXME: AuthDataService should not be responsible for loading organizations.
    //  Instead, OrgService should ensure orgs are loaded when its organizationArray is first accessed.
    //  Best practice is to avoid having consumers of services inspecting the internal state
    //  of other services.
    if (!this.organizationArray() || this.organizationArray().length < 1) {
      this.orgService.setOrganizations().catch(err => {
        console.error('Error loading organizations in AuthDataService constructor:', err);
      });
    }
  }

  // FIXME: AuthDataService should not be responsible for loading organizations.
  public async loadOrganizations() {
    return await this.orgService.setOrganizations();
  }
}
